import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Activity, CreditCard } from "lucide-react";
import { fmtDateTime as fmtDate, fmtMoney } from "@/lib/intl";
import StripePayments from "@/components/StripePayments";

function isDateField(key) {
  return /(date|time|At)$/i.test(key);
}

function isMoneyField(key) {
  return /(price|amount|paid|tax|total|subtotal|discount)/i.test(key);
}

function renderValue(key, val) {
  if (val === null || val === undefined || val === "") return <span className="text-zinc-400">—</span>;
  if (typeof val === "boolean") {
    return (
      <Badge variant={val ? "default" : "outline"} className={val ? "bg-zinc-900 text-white" : ""}>
        {val ? "true" : "false"}
      </Badge>
    );
  }
  if (Array.isArray(val) || typeof val === "object") {
    return (
      <pre className="font-mono-num text-xs bg-zinc-50 border border-zinc-200 rounded p-2 whitespace-pre-wrap break-all">
        {JSON.stringify(val, null, 2)}
      </pre>
    );
  }
  if (key && isDateField(key) && typeof val === "string" && val.length >= 10) {
    return <span className="font-mono-num">{fmtDate(val)}</span>;
  }
  if (key && isMoneyField(key)) {
    return <span className="font-mono-num">{fmtMoney(val)}</span>;
  }
  if (typeof val === "number") {
    return <span className="font-mono-num">{val}</span>;
  }
  return <span className="break-all">{String(val)}</span>;
}

function FieldRow({ k, v }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 py-2 border-b border-zinc-100 last:border-0">
      <div className="text-2xs font-semibold uppercase tracking-wider text-zinc-500 sm:pt-0.5">
        {k}
      </div>
      <div className="sm:col-span-2 text-sm text-zinc-900">{renderValue(k, v)}</div>
    </div>
  );
}

export default function OrderDetails({ order, open, onOpenChange, onFindRelated, onDiagnoseCustomer }) {
  if (!order) return null;

  const groups = {
    Identity: ["id", "firstName", "lastName", "email", "phone"],
    Order: ["time", "status", "certificate", "couponCode", "notes"],
    Financial: [
      "subtotal",
      "tax",
      "total",
      "amountPaid",
      "discount",
      "currency",
      "processor",
    ],
    Address: ["address", "city", "state", "zip", "country"],
  };

  const grouped = new Set();
  Object.values(groups).forEach((arr) => arr.forEach((k) => grouped.add(k)));
  const remaining = Object.keys(order).filter(
    (k) => !grouped.has(k) && k !== "products" && k !== "forms"
  );

  const products = order.products || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="order-details-dialog"
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {[order.firstName, order.lastName].filter(Boolean).join(" ") ||
              `Order #${order.id}`}
          </DialogTitle>
          <DialogDescription className="font-mono-num text-xs">
            #{order.id} · {fmtDate(order.time)} · {fmtMoney(order.total)} ·{" "}
            {order.status || "—"}
          </DialogDescription>
          {onFindRelated && (order.email || order.lastName) && (
            <div className="pt-2 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-300 hover:bg-zinc-100 h-8"
                onClick={() => onFindRelated(order)}
                data-testid="find-related-orders-btn"
                title="Search all Acuity orders by this customer's email — surfaces every recurring/subscription renewal payment"
              >
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Find all orders by this customer
              </Button>
              {onDiagnoseCustomer && order.email && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 h-8"
                  onClick={() => onDiagnoseCustomer(order)}
                  data-testid="diagnose-customer-btn"
                  title="Probe every Acuity endpoint for this customer's data — useful when expected records (e.g. subscription renewals) are missing"
                >
                  <Activity className="h-3.5 w-3.5 mr-1.5" />
                  Diagnose in Acuity
                </Button>
              )}
            </div>
          )}
        </DialogHeader>

        <Tabs defaultValue="organized" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="bg-zinc-100">
            <TabsTrigger value="organized" data-testid="order-tab-organized">
              Organized
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="order-tab-products">
              Products
              {products.length ? (
                <span className="ml-1.5 text-2xs font-mono-num text-zinc-500">
                  {products.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="stripe" data-testid="order-tab-stripe">
              <CreditCard className="h-3.5 w-3.5 mr-1" />
              Stripe Payments
            </TabsTrigger>
            <TabsTrigger value="raw" data-testid="order-tab-raw">
              Raw JSON
            </TabsTrigger>
          </TabsList>

          <TabsContent value="organized" className="overflow-y-auto pr-1 thin-scroll">
            <div className="space-y-6 mt-4">
              {Object.entries(groups).map(([title, keys]) => {
                const present = keys.filter((k) => k in order);
                if (!present.length) return null;
                return (
                  <section key={title}>
                    <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                      {title}
                    </div>
                    <div className="border border-zinc-200 rounded-md bg-white px-4">
                      {present.map((k) => (
                        <FieldRow key={k} k={k} v={order[k]} />
                      ))}
                    </div>
                  </section>
                );
              })}
              {remaining.length > 0 && (
                <section>
                  <div className="text-2xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                    Additional fields
                  </div>
                  <div className="border border-zinc-200 rounded-md bg-white px-4">
                    {remaining.map((k) => (
                      <FieldRow key={k} k={k} v={order[k]} />
                    ))}
                  </div>
                </section>
              )}
            </div>
          </TabsContent>

          <TabsContent value="products" className="overflow-y-auto pr-1 thin-scroll">
            <div className="mt-4 space-y-4">
              {products.length === 0 && (
                <div className="text-sm text-zinc-500 border-2 border-dashed border-zinc-200 rounded-md p-8 text-center bg-zinc-50">
                  No line items on this order.
                </div>
              )}
              {products.map((p, idx) => (
                <section
                  key={p.id || idx}
                  className="border border-zinc-200 rounded-md bg-white"
                >
                  <div className="px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <div className="font-medium text-zinc-900">
                      {p.name || `Item #${idx + 1}`}
                    </div>
                    <div className="font-mono-num text-xs text-zinc-500">
                      ×{p.quantity || 1}
                      {p.id ? ` · #${p.id}` : ""}
                    </div>
                  </div>
                  <div className="px-4">
                    {Object.entries(p).map(([k, v]) => (
                      <FieldRow key={k} k={k} v={v} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="stripe" className="overflow-y-auto pr-1 thin-scroll">
            <div className="mt-4">
              <StripePayments email={order.email} />
            </div>
          </TabsContent>

          <TabsContent value="raw" className="overflow-y-auto pr-1 thin-scroll">
            <pre
              data-testid="order-raw-json"
              className="mt-4 text-xs font-mono-num bg-zinc-950 text-zinc-100 p-4 rounded-md overflow-auto"
            >
              {JSON.stringify(order, null, 2)}
            </pre>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
